import { useState, FocusEvent } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  CardNumberElementChangeEvent,
  IFFFChangeHandlerEvent,
  IFFFRecurlyErrorHandler,
  IndividualElementChangeEvent,
  IRecurlyFieldState,
} from '../../../../interfaces';

function isRecurlyEvent(
  e: IFFFChangeHandlerEvent
): e is CardNumberElementChangeEvent | IndividualElementChangeEvent {
  const CNECEHasFocus = (e as CardNumberElementChangeEvent).focus !== undefined;
  const CNECEHasValid = (e as CardNumberElementChangeEvent).valid !== undefined;
  const IECEHasFocus = (e as IndividualElementChangeEvent).focus !== undefined;
  const IECEHasValid = (e as IndividualElementChangeEvent).valid !== undefined;
  return CNECEHasFocus && CNECEHasValid && IECEHasFocus && IECEHasValid;
}

const basicErrorHandler = (change: IFFFChangeHandlerEvent, fieldName: string): string => {
  if (isRecurlyEvent(change) && change.valid) return '';
  return `Enter valid ${fieldName || 'value'}`;
};

export const creditCardErrorHandler: IFFFRecurlyErrorHandler = (change) =>
  basicErrorHandler(change, 'credit card');
export const cvvErrorHandler: IFFFRecurlyErrorHandler = (change) =>
  basicErrorHandler(change, 'cvv number');
export const monthErrorHandler: IFFFRecurlyErrorHandler = (change) =>
  basicErrorHandler(change, 'month');
export const yearErrorHandler: IFFFRecurlyErrorHandler = (change) =>
  basicErrorHandler(change, 'year');

const useRecurlyFieldState = (messageErrorHandler: IFFFRecurlyErrorHandler, inputName: string): IRecurlyFieldState => {
  const [isTouched, setIsTouched] = useState(false);
  const [lastPostSubmitError, setLastPostSubmitError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [currentState, setCurrentState] = useState<IFFFChangeHandlerEvent | { valid: boolean }>({
    valid: false,
  });

  const changeHandler = (change: IFFFChangeHandlerEvent) => {
    const currentErrorMsg = messageErrorHandler(change);
    setErrorMsg(currentErrorMsg);
    setCurrentState(change);
  };

  const blurHandler = () => {
    if (!isTouched) setIsTouched(true);
  };

  const focusHandler = () => {
    datadogLogs.logger.info(`[GTB::V2]: Focus on ${inputName.toUpperCase()} input`);
  }

  const onSubmit = (error: string) => {
    setErrorMsg(error);
    setLastPostSubmitError(error);
  };

  return {
    currentState,
    errorMsg,
    hasError: errorMsg !== '' && isTouched,
    isTouched,
    changeHandler,
    blurHandler,
    focusHandler,
    onSubmit,
    lastPostSubmitError,
  };
};

export default useRecurlyFieldState;
